import axios from 'axios';

export interface StructuredError {
  message: string;
  code: string;
  statusCode: number;
  userFriendly: string;
  retryable: boolean;
}

/**
 * Handle task update errors with structured error messages
 */
export function handleTaskUpdateError(error: unknown): string {
  const structured = extractError(error);
  
  switch (structured.code) {
    case 'TASK_NOT_FOUND':
      return 'Aufgabe nicht gefunden. Möglicherweise wurde sie bereits gelöscht.';
    case 'VALIDATION_ERROR':
      return 'Ungültige Aufgabendaten. Bitte Eingaben überprüfen.';
    case 'NETWORK_ERROR':
      return 'Verbindungsfehler. Bitte erneut versuchen.';
    case 'SERVER_ERROR':
      return structured.userFriendly;
    default:
      return structured.userFriendly;
  }
}

/**
 * Handle booking update errors
 */
export function handleBookingUpdateError(error: unknown): string {
  const structured = extractError(error);
  
  switch (structured.code) {
    case 'BOOKING_NOT_FOUND':
      return 'Buchung nicht gefunden.';
    case 'BOOKING_DATE_VALIDATION_ERROR':
      return 'Ungültiges Buchungsdatum. Bitte Eingaben überprüfen.';
    case 'BOOKING_GUEST_VALIDATION_ERROR':
      return 'Ungültige Gästedaten. Bitte Eingaben überprüfen.';
    case 'NETWORK_ERROR':
      return 'Verbindungsfehler. Bitte erneut versuchen.';
    default:
      return structured.userFriendly;
  }
}

/**
 * Extract structured error information from unknown error
 */
function extractError(error: unknown): StructuredError {
  // Network error
  if (axios.isAxiosError(error) || (typeof error === 'object' && error !== null && 'response' in error)) {
    const requestError = error as { message: string; response?: { status: number; data?: any } };
    if (!requestError.response) {
      return {
        message: requestError.message,
        code: 'NETWORK_ERROR',
        statusCode: 0,
        userFriendly: 'Verbindungsfehler. Bitte erneut versuchen.',
        retryable: true
      };
    }
    
    // HTTP error response
    const status = requestError.response.status;
    const data = requestError.response.data;
    
    return {
      message: data?.message || data?.error?.message || requestError.message,
      code: data?.error || `HTTP_${status}`,
      statusCode: status,
      userFriendly: getUserFriendlyMessage(status),
      retryable: status >= 500 || status === 429
    };
  }
  
  // Standard Error
  if (error instanceof Error) {
    return {
      message: error.message,
      code: 'UNKNOWN_ERROR',
      statusCode: 500,
      userFriendly: error.message,
      retryable: false
    };
  }
  
  // Fallback
  return {
    message: String(error),
    code: 'UNKNOWN_ERROR',
    statusCode: 500,
    userFriendly: 'Ein unerwarteter Fehler ist aufgetreten.',
    retryable: false
  };
}

function getUserFriendlyMessage(status: number): string {
  // HTTP causes remain in diagnostics; raw backend text may be English or duplicate the status.
  if (status >= 500) return 'Der Server konnte die Anfrage nicht bearbeiten. Bitte erneut versuchen.';
  switch (status) {
    case 400: return 'Ungültige Anfrage. Bitte Eingaben überprüfen.';
    case 401: return 'Nicht autorisiert. Bitte erneut anmelden.';
    case 403: return 'Keine Berechtigung für diese Aktion.';
    case 404: return 'Ressource nicht gefunden.';
    case 429: return 'Zu viele Anfragen. Bitte langsamer.';
    case 500:
    case 502:
    case 503:
    case 504: return 'Server-Fehler. Bitte später erneut versuchen.';
    default: return 'Ein Fehler ist aufgetreten.';
  }
}

/** Support requests use both Axios errors and SupportServiceError. Keep the cause visible. */
export function handleSupportError(error: unknown, action: string): string {
  const value = error as { statusCode?: number; code?: string; details?: { message?: string; error?: string }; message?: string } | null;
  const status = value?.statusCode;
  const cause = status !== undefined
    ? status === 0 ? 'Verbindung zum Dienst unterbrochen.'
      // The support routes answer 4xx with a German cause (e.g. the closed WhatsApp window); 5xx stay generic.
      : (status >= 400 && status < 500 && (value?.details?.message || value?.message)) || getUserFriendlyMessage(status)
    : extractError(error).userFriendly;
  const code = value?.details?.error || value?.code;
  return `${action}: ${cause}${code ? ` (${code})` : ''}`;
}

/** Preserve service validation causes; server failures use the generic German message. */
export function handleEmailError(error: unknown, context: string): string {
  const structured = extractError(error);
  const message = structured.statusCode >= 400 && structured.statusCode < 500
    ? structured.message || structured.userFriendly
    : structured.userFriendly;
  return `${context}: ${message}`;
}

/** Expected absence of arrivals is information, not a failed cleaning plan. */
export function handlePreviewLoadError(error: unknown): { message: string; isNotice: boolean } {
  const response = (error as { response?: { data?: any } })?.response;
  const data = response?.data;
  const code = data?.code || data?.error?.code || data?.error;
  const message = data?.message || data?.error?.message || data?.error || (error instanceof Error ? error.message : '');
  if (code === 'NO_BOOKINGS_FOUND' || message === 'No bookings found for the specified date') {
    return { message: 'Für diesen Tag liegen keine Anreisen vor. Daher wird kein Reinigungsplan erzeugt.', isNotice: true };
  }
  return { message: response ? handleBookingUpdateError(error) : 'Vorschau konnte nicht geladen werden. Bitte erneut versuchen.', isNotice: false };
}
